/**
 * Dashboard — IA screen 3, owned by F29/M29 (the shell owns it; the numbers
 * belong to the features they come from).
 *
 * "Health-at-a-glance" per 08 §4. Every tile is a **link to the list behind it**,
 * because 08 §7's content rule is that every count links to its records — a
 * dead-end number invites a screenshot and a question rather than an action.
 */
import { Button, Card, DefinitionList, EmptyState, HelpText, Skeleton } from '@dedupr/ui';
import './Dashboard.css';
import { api, ErrorState, useQuery } from '@dedupr/app-kit';
import { ApiError } from '@dedupr/api-client';
import { openLocalStore } from '../local/store.js';

interface Tile {
  label: string;
  /** Null when the figure could not be loaded — never silently rendered as 0. */
  count: number | null;
  detail: string;
  route: string;
  tone: 'default' | 'attention';
}

export function Dashboard({
  orgId,
  onNavigate,
}: {
  orgId: string | undefined;
  onNavigate: (route: string) => void;
}) {
  const data = useQuery(async () => {
    if (!orgId) return null;
    // Settled, not `all`: one failing endpoint must degrade its own tile rather
    // than blank the dashboard. A dashboard that disappears because a single
    // count failed is worse than one honest gap.
    const [sources, findings, pairs, actions] = await Promise.allSettled([
      api.listSources({ org_id: orgId }),
      api.listFindings({ org_id: orgId, state: 'open' }),
      api.listCandidatePairs({ org_id: orgId, tier: 'review', undecided: true }),
      api.listActions({ org_id: orgId, state: 'proposed' }),
    ]);
    const val = <T,>(r: PromiseSettledResult<T>) => (r.status === 'fulfilled' ? r.value : null);
    /* ⚠️ **`not_provisioned` is a KNOWN STATE, not a failure, and collapsing the two was rule 5 in
       a new place.** `val()` turns every rejection into `null`, so a client whose data plane has
       simply never been stamped out (S6) saw four tiles reading "Could not load this figure" — the
       same words a real outage produces. Found by signing in live as a client with an org and no
       `deployment_record`: the router sends them here deliberately (`stay` is F26's setup-journey
       state) and this screen then told them something was broken.
       Only when EVERY figure says so: one endpoint answering `not_provisioned` while others return
       data would be a genuine inconsistency, and must keep reading as the error it is. */
    const reason = (r: PromiseSettledResult<unknown>) =>
      r.status === 'rejected' && r.reason instanceof ApiError ? r.reason.code : null;
    const settled = [sources, findings, pairs, actions];
    const notProvisioned = settled.every((r) => reason(r) === 'not_provisioned');
    return {
      sources: val(sources),
      findings: val(findings),
      pairs: val(pairs),
      actions: val(actions),
      notProvisioned,
    };
  }, [orgId]);

  /* ⚠️ **Z-1.** This branch used to render "No organisation yet" and nothing else, so a local-mode
     user's FIRST screen — the app's landing route — was a single sentence about a state they are not
     in and cannot reach. The whole product looked like it did not exist.
     Local mode has no org by design (F15: the engine runs on the device, there is nothing to belong
     to), so "no org" is not an error condition here — it is the normal case, and the numbers come
     from IndexedDB instead of `/v1/`. */
  if (!orgId) return <LocalDashboard onNavigate={onNavigate} />;

  if (data.status === 'loading') {
    return (
      <div className="web-dash" aria-busy="true">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rect" height="6rem" />
        ))}
      </div>
    );
  }

  if (data.status === 'error') {
    return <ErrorState code={data.errorCode} message={data.errorMessage} onRetry={data.reload} />;
  }

  // Belt as well as braces: useQuery now reports a deps change as `loading`, but
  // this fetcher can also legitimately resolve to null (no org in scope), and a
  // screen should not dereference a nullable success either way.
  const d = data.data;
  if (!d) {
    return (
      <div className="web-dash" aria-busy="true">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rect" height="6rem" />
        ))}
      </div>
    );
  }

  /* Said plainly, and never as a number. A count of zero here would be a claim about their data;
     the truth is that there is nowhere to count yet. */
  if (d.notProvisioned) {
    return (
      <EmptyState
        title="Your workspace is still being set up"
        description="Your organisation exists, but the infrastructure that holds your records has not been created yet — so there is nothing to show rather than nothing to find. Nobody needs to do anything; this screen fills in once it is ready."
      />
    );
  }

  const unhealthy =
    d.sources?.data.filter((s) => s.state === 'auth_error' || s.state === 'degraded').length ?? null;

  const tiles: Tile[] = [
    {
      label: 'Connected sources',
      count: d.sources?.data.length ?? null,
      detail:
        unhealthy && unhealthy > 0
          ? `${unhealthy} need attention — findings from them may be stale`
          : 'All syncing normally',
      route: '/sources',
      tone: unhealthy && unhealthy > 0 ? 'attention' : 'default',
    },
    {
      label: 'Open findings',
      count: d.findings?.data.length ?? null,
      detail: 'Ranked next steps for your data',
      route: '/findings',
      tone: 'default',
    },
    {
      label: 'Pairs to review',
      count: d.pairs?.data.length ?? null,
      detail: 'Undecided duplicates in the review tier',
      route: '/matches',
      tone: 'default',
    },
    {
      label: 'Actions awaiting you',
      count: d.actions?.data.length ?? null,
      detail: 'Proposed changes, none applied without approval',
      route: '/actions',
      tone: d.actions && d.actions.data.length > 0 ? 'attention' : 'default',
    },
  ];

  const anyMissing = tiles.some((t) => t.count === null);

  return (
    <>
      <div className="web-dash">
        {tiles.map((tile) => (
          <button
            key={tile.label}
            type="button"
            className={['web-dash__tile', tile.tone === 'attention' ? 'web-dash__tile--attention' : null]
              .filter(Boolean)
              .join(' ')}
            onClick={() => onNavigate(tile.route)}
            // Explicit, rather than letting the browser concatenate the spans:
            // adjacent inline elements produce "1Actions awaiting you" with no
            // separator, which a screen reader announces as "1Actions". The
            // announcement order is also deliberate — label first, then the
            // number, because "Connected sources, 3" is a fact and "3" alone is
            // a riddle.
            aria-label={`${tile.label}: ${
              tile.count === null ? 'could not be loaded' : tile.count
            }. ${tile.count === null ? '' : tile.detail}`.trim()}
          >
            {/* Label, then figure, then caption — the prototype's stat-card
                order. The build had the figure first, which inverts the
                hierarchy: a 30px number with no preceding label is a riddle, and
                four of them in a row read as a scoreboard rather than as four
                different things. The accessible name above is already label-first
                for the same reason, so this only aligns what is seen with what is
                announced (audit §2.9). */}
            <span className="web-dash__label">{tile.label}</span>
            <span className="web-dash__count">
              {tile.count === null ? '—' : new Intl.NumberFormat().format(tile.count)}
            </span>
            <span className="web-dash__detail">
              {tile.count === null ? 'Could not load this figure' : tile.detail}
            </span>
          </button>
        ))}
      </div>

      {anyMissing ? (
        <HelpText tone="error" role="alert">
          One or more figures could not be loaded. They are shown as “—” rather than zero, because a
          zero here would read as “nothing to do”.
        </HelpText>
      ) : null}

      <Card title="What is not here yet" headingLevel={2}>
        <DefinitionList
          items={[
            {
              term: 'Savings to date',
              value: 'Not shown',
              hint: 'Needs a configured rate; a monetary figure without its formula is not shown at all (08 §7, ADR-045).',
            },
            {
              term: 'Trend over time',
              value: 'Not shown',
              hint: 'Needs more than one sync run to compare against.',
            },
          ]}
        />
      </Card>
    </>
  );
}

/**
 * The dashboard for a device with no organisation — F15's local mode.
 *
 * Same tile grid and the same rules as the hosted one: a figure that could not be read renders as
 * "—" and never as 0, and every tile links to the screen that holds the records behind it.
 *
 * ⚠️ **Every route here points at a screen that actually works today** (`/sources`, `/matches`).
 * Linking a tile to `/findings` or `/actions` would put a live number in front of a placeholder,
 * which is worse than not showing the number — the count would be real and the destination empty.
 * When Z-4 builds `/actions`, "merges applied" moves there; until then it is a line of text rather
 * than a link to nowhere.
 */
function LocalDashboard({ onNavigate }: { onNavigate: (route: string) => void }) {
  const data = useQuery(async () => {
    const store = await openLocalStore();
    try {
      const [sources, records, run, golden] = await Promise.all([
        store.listSources(),
        store.allRecords(),
        store.latestRun(),
        Promise.resolve(null),
      ]);
      const [pairs, entities, decisions, applied] = run
        ? await Promise.all([
            store.pairsFor(run.id),
            store.entitiesFor(run.id),
            store.decisions(),
            store.goldenFor(run.id),
          ])
        : [[], [], [], []];
      void golden;
      return { sources, records: records.length, pairs, entities, decisions, applied };
    } finally {
      /* Closed even on the error path: an open connection blocks a version upgrade indefinitely,
         which is how a DB_VERSION bump hangs for a user who left a tab open. */
      store.close();
    }
  }, []);

  if (data.status === 'loading') {
    return (
      <div className="web-dash" aria-busy="true">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rect" height="6rem" />
        ))}
      </div>
    );
  }

  if (data.status === 'error') {
    /* IndexedDB is genuinely refused in Safari private browsing and Firefox's "never remember
       history", so this is a real state rather than a defensive branch. */
    return <ErrorState code={data.errorCode} message={data.errorMessage} onRetry={data.reload} />;
  }

  const d = data.data;

  /* Nothing loaded yet. ⚠️ This is the state a first-time visitor lands in, and the one thing it
     must do is say where to start — the previous version of this screen did not, which is how the
     product read as "one upload box and nothing else". */
  if (!d || d.sources.length === 0) {
    return (
      <Card title="Start with a file">
        <p className="web-dash__intro">
          Load a CSV or Excel export and Dedupr will map the columns, profile every field and find the
          duplicates — all in this browser. Nothing is uploaded.
        </p>
        {/* The catalogue Button, not a bespoke CTA. A hand-rolled one needed accent tokens that do
            not exist (`--accent-solid-bg` and friends) — lint:tokens caught the invention, and the
            answer was the primitive that already solves this rather than three new tokens. */}
        <Button onClick={() => onNavigate('/local')}>Choose a file</Button>
      </Card>
    );
  }

  const clusters = d.entities.filter((e) => e.members.length > 1);
  const undecided = d.pairs.filter((pair) => !d.decisions.some((dec) => dec.pair_id === pair.id));
  const liveMerges = d.applied.filter((g) => !g.rolled_back_at);

  const tiles: Tile[] = [
    {
      label: 'Files loaded',
      count: d.sources.length,
      detail: `${new Intl.NumberFormat().format(d.records)} records read on this device`,
      route: '/sources',
      tone: 'default',
    },
    {
      label: 'Duplicate groups',
      count: clusters.length,
      detail:
        clusters.length > 0
          ? `${new Intl.NumberFormat().format(clusters.reduce((n, e) => n + e.members.length, 0))} records involved`
          : 'Nothing in these files looked like a duplicate',
      route: '/matches',
      tone: 'default',
    },
    {
      label: 'Still to decide',
      count: undecided.length,
      detail: undecided.length > 0 ? 'Each one waits for you — nothing decides itself' : 'Everything found has been decided',
      route: '/matches',
      tone: undecided.length > 0 ? 'attention' : 'default',
    },
    {
      label: 'Merges applied',
      count: liveMerges.length,
      detail:
        liveMerges.length > 0
          ? 'Reflected in the file you download; undo any of them'
          : 'None yet — a merge changes only your download',
      route: '/matches',
      tone: 'default',
    },
  ];

  return (
    <>
      <div className="web-dash">
        {tiles.map((tile) => (
          <button
            key={tile.label}
            type="button"
            className={['web-dash__tile', tile.tone === 'attention' ? 'web-dash__tile--attention' : null]
              .filter(Boolean)
              .join(' ')}
            onClick={() => onNavigate(tile.route)}
            /* Label first, then the figure — same reasoning as the hosted tiles: "Files loaded, 2"
               is a fact and "2" alone is a riddle. Written out rather than left to the browser's
               concatenation, which announces "2Files loaded". */
            aria-label={`${tile.label}: ${tile.count}. ${tile.detail}`}
          >
            <span className="web-dash__label">{tile.label}</span>
            <span className="web-dash__count">{new Intl.NumberFormat().format(tile.count ?? 0)}</span>
            <span className="web-dash__detail">{tile.detail}</span>
          </button>
        ))}
      </div>

      <Card title="This is local mode" headingLevel={2}>
        <DefinitionList
          items={[
            {
              term: 'Where your data is',
              value: 'On this device only',
              hint: 'The engine runs in your browser. No records are uploaded, and closing the tab does not lose them — they are kept in this browser\'s storage.',
            },
            {
              term: 'Connected systems',
              value: 'Not connected yet',
              /* ⚠️ Was "Not in local mode — pulling needs an account". Both halves became wrong the
                 moment an Entry/Sandbox client could reach this screen: they HAVE an account, and
                 pulling is now built (ADR-074) and reachable on Sources. What is actually missing is
                 an OAuth app for the provider (B-130), which is a deployment fact rather than
                 anything about their plan. Found by signing in as a seeded entry_sandbox client
                 against a real local Supabase — the copy read as a tier limit to someone it did not
                 apply to. */
              hint: 'Connect Salesforce or HubSpot on the Sources screen and you can pull records straight onto this device. The credential is held by the connector service, never by your browser.',
            },
          ]}
        />
        <HelpText>
          Load another file at any time — Dedupr matches across everything on this device, not just
          within one file.
        </HelpText>
      </Card>
    </>
  );
}
